<script setup lang="ts">
import { computed, ref } from "vue"
import FormSection from "@/forms/components/FormSection.vue"
import FormField from "@/forms/components/FormField.vue"
import TagTaglist from "../../tag/TagTaglist.vue"
import CompanyTaglist from "../../companies/CompanyTaglist.vue"
import GameSeriesTaglist from "../../gameseries/GameSeriesTaglist.vue"
import GameTaglist from "../../games/GameTaglist.vue"
import GameConsoleTaglist from "../../gameconsole/GameConsoleTaglist.vue"
import TriStateToggle from "./TriStateToggle.vue"
import ArrayFieldModeToggle from "./ArrayFieldModeToggle.vue"
import type { BatchEditSession } from "./useBatchEditSession"
import type { ArrayField, ArrayMode, ScalarField } from "./batchEditItem"

const props = defineProps<{ session: BatchEditSession }>()

const overwriteOn = ref<Set<ScalarField>>(new Set())

function isMixed(field: ScalarField): boolean {
  return props.session.mixedFields.value.has(field)
}

function scalarDisabled(field: ScalarField): boolean {
  return isMixed(field) && !overwriteOn.value.has(field)
}

function scalarPlaceholder(field: ScalarField): string {
  return isMixed(field) ? "Gemischt — nicht ändern" : ""
}

function toggleOverwrite(field: ScalarField): void {
  const next = new Set(overwriteOn.value)
  if (next.has(field)) next.delete(field)
  else next.add(field)
  overwriteOn.value = next
}

function initialScalar(field: ScalarField): string {
  if (isMixed(field)) return ""
  const first = props.session.items.value[0]
  return first?.[field] ?? ""
}

function readScalar(field: ScalarField): string {
  const patched = props.session.scalarPatch.value[field]
  return patched ?? initialScalar(field)
}

function writeScalar(field: ScalarField, value: string): void {
  props.session.scalarPatch.value = { ...props.session.scalarPatch.value, [field]: value }
}

function arrayMode(field: ArrayField): ArrayMode {
  return props.session.arrayPatches.value[field]?.mode ?? "noop"
}

function setArrayMode(field: ArrayField, mode: ArrayMode): void {
  const current = props.session.arrayPatches.value[field]
  const values = current?.values ?? []
  const patches = { ...props.session.arrayPatches.value }
  if (mode === "noop") {
    delete patches[field]
  } else {
    patches[field] = { mode, values }
  }
  props.session.arrayPatches.value = patches
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function readArrayValues(field: ArrayField): any[] {
  return props.session.arrayPatches.value[field]?.values ?? []
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function writeArrayValues(field: ArrayField, values: any[]): void {
  const current = props.session.arrayPatches.value[field]
  const mode = current?.mode ?? "add"
  props.session.arrayPatches.value = {
    ...props.session.arrayPatches.value,
    [field]: { mode, values }
  }
}

const hasReplaceMode = computed(() =>
  Object.values(props.session.arrayPatches.value).some(p => p && p.mode === "replace")
)
</script>

<template>
  <div class="flex flex-col gap-6">
    <FormSection id="batch-filename" title="Dateinamen">
      <FormField name="filenamePattern" label="Serienumbenennung">
        <input
          data-testid="batch-filename-pattern"
          class="input input-bordered w-full"
          :value="session.filenamePattern.value"
          placeholder="Leer lassen — Dateinamen nicht ändern"
          @input="session.filenamePattern.value = ($event.target as HTMLInputElement).value"
        >
        <p class="text-xs text-base-content/60 mt-1">
          Nummeriert in Listenreihenfolge, Endung bleibt. <code>{n}</code> setzt die Nummer an diese Stelle,
          sonst wird sie angehängt.
        </p>
      </FormField>
    </FormSection>

    <FormSection id="batch-rights" title="Rechte & Quelle">
      <FormField name="copyright" label="Copyright-Hinweis">
        <template v-if="isMixed('copyright')" #label-trailing>
          <button
            data-testid="batch-copyright-overwrite"
            type="button"
            class="btn btn-ghost btn-xs"
            @click="toggleOverwrite('copyright')"
          >
            {{ overwriteOn.has('copyright') ? "Bearbeiten an" : "Überschreiben …" }}
          </button>
        </template>
        <input
          data-testid="batch-copyright"
          type="text"
          class="input input-bordered w-full"
          :value="readScalar('copyright')"
          :disabled="scalarDisabled('copyright')"
          :placeholder="scalarPlaceholder('copyright')"
          @input="writeScalar('copyright', ($event.target as HTMLInputElement).value)"
        >
      </FormField>
      <FormField name="copyrightUrl" label="Copyright-URL">
        <template v-if="isMixed('copyrightUrl')" #label-trailing>
          <button
            type="button"
            class="btn btn-ghost btn-xs"
            @click="toggleOverwrite('copyrightUrl')"
          >
            {{ overwriteOn.has('copyrightUrl') ? "Bearbeiten an" : "Überschreiben …" }}
          </button>
        </template>
        <input
          type="url"
          class="input input-bordered w-full"
          :value="readScalar('copyrightUrl')"
          :disabled="scalarDisabled('copyrightUrl')"
          :placeholder="scalarPlaceholder('copyrightUrl')"
          @input="writeScalar('copyrightUrl', ($event.target as HTMLInputElement).value)"
        >
      </FormField>
      <FormField name="license" label="Lizenz">
        <template v-if="isMixed('license')" #label-trailing>
          <button
            type="button"
            class="btn btn-ghost btn-xs"
            @click="toggleOverwrite('license')"
          >
            {{ overwriteOn.has('license') ? "Bearbeiten an" : "Überschreiben …" }}
          </button>
        </template>
        <input
          type="text"
          class="input input-bordered w-full"
          :value="readScalar('license')"
          :disabled="scalarDisabled('license')"
          :placeholder="scalarPlaceholder('license')"
          @input="writeScalar('license', ($event.target as HTMLInputElement).value)"
        >
      </FormField>
      <FormField name="licenseUrl" label="Lizenz-URL">
        <template v-if="isMixed('licenseUrl')" #label-trailing>
          <button
            type="button"
            class="btn btn-ghost btn-xs"
            @click="toggleOverwrite('licenseUrl')"
          >
            {{ overwriteOn.has('licenseUrl') ? "Bearbeiten an" : "Überschreiben …" }}
          </button>
        </template>
        <input
          type="url"
          class="input input-bordered w-full"
          :value="readScalar('licenseUrl')"
          :disabled="scalarDisabled('licenseUrl')"
          :placeholder="scalarPlaceholder('licenseUrl')"
          @input="writeScalar('licenseUrl', ($event.target as HTMLInputElement).value)"
        >
      </FormField>
      <FormField name="sourceUrl" label="Quelle-URL">
        <template v-if="isMixed('sourceUrl')" #label-trailing>
          <button
            type="button"
            class="btn btn-ghost btn-xs"
            @click="toggleOverwrite('sourceUrl')"
          >
            {{ overwriteOn.has('sourceUrl') ? "Bearbeiten an" : "Überschreiben …" }}
          </button>
        </template>
        <input
          type="url"
          class="input input-bordered w-full"
          :value="readScalar('sourceUrl')"
          :disabled="scalarDisabled('sourceUrl')"
          :placeholder="scalarPlaceholder('sourceUrl')"
          @input="writeScalar('sourceUrl', ($event.target as HTMLInputElement).value)"
        >
      </FormField>
      <FormField name="watermark" label="Watermark">
        <TriStateToggle
          data-testid="batch-watermark"
          :model-value="session.watermarkMode.value"
          @update:model-value="session.watermarkMode.value = $event"
        />
      </FormField>
    </FormSection>

    <FormSection id="batch-tags" title="Tags">
      <FormField name="tags" label="Tags">
        <ArrayFieldModeToggle
          data-testid="batch-tags-mode"
          :model-value="arrayMode('tags')"
          @update:model-value="setArrayMode('tags', $event)"
        />
        <div v-if="arrayMode('tags') !== 'noop'" class="mt-2">
          <TagTaglist
            data-testid="batch-tags-taglist"
            :model-value="readArrayValues('tags')"
            @update:model-value="writeArrayValues('tags', $event)"
          />
        </div>
      </FormField>
    </FormSection>

    <FormSection id="batch-assignments" title="Zuordnungen">
      <FormField name="games" label="Spiele">
        <ArrayFieldModeToggle
          :model-value="arrayMode('games')"
          @update:model-value="setArrayMode('games', $event)"
        />
        <div v-if="arrayMode('games') !== 'noop'" class="mt-2">
          <GameTaglist
            :model-value="readArrayValues('games')"
            @update:model-value="writeArrayValues('games', $event)"
          />
        </div>
      </FormField>
      <FormField name="gameSeries" label="Spielereihen">
        <ArrayFieldModeToggle
          :model-value="arrayMode('gameSeries')"
          @update:model-value="setArrayMode('gameSeries', $event)"
        />
        <div v-if="arrayMode('gameSeries') !== 'noop'" class="mt-2">
          <GameSeriesTaglist
            :model-value="readArrayValues('gameSeries')"
            @update:model-value="writeArrayValues('gameSeries', $event)"
          />
        </div>
      </FormField>
      <FormField name="gameConsoles" label="Konsolen">
        <ArrayFieldModeToggle
          :model-value="arrayMode('gameConsoles')"
          @update:model-value="setArrayMode('gameConsoles', $event)"
        />
        <div v-if="arrayMode('gameConsoles') !== 'noop'" class="mt-2">
          <GameConsoleTaglist
            :model-value="readArrayValues('gameConsoles')"
            @update:model-value="writeArrayValues('gameConsoles', $event)"
          />
        </div>
      </FormField>
      <FormField name="companies" label="Firmen">
        <ArrayFieldModeToggle
          :model-value="arrayMode('companies')"
          @update:model-value="setArrayMode('companies', $event)"
        />
        <div v-if="arrayMode('companies') !== 'noop'" class="mt-2">
          <CompanyTaglist
            :model-value="readArrayValues('companies')"
            @update:model-value="writeArrayValues('companies', $event)"
          />
        </div>
      </FormField>
    </FormSection>

    <div v-if="hasReplaceMode" class="alert alert-warning text-sm">
      Ersetzt bestehende Einträge bei allen Dateien. Nicht rückgängig.
    </div>
  </div>
</template>
